"use client";

import { useEffect, useMemo, useState } from 'react'
import { Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, AreaChart } from 'recharts'
import { useFinance, latestMonth } from '@/lib/finance/finance-data-context'
import { contributionMatrix } from '@/lib/finance/asof'
import { dateToMs, fmtDate, fmtMsDate, fmtUsd, fmtUsdCompact } from '@/lib/finance/format'
import {
  ACCOUNT_GROUPS,
  LIABILITY_GROUPS,
  accountGroupColor,
  accountGroupOf,
} from '@/lib/finance/categories'
import { Card, LegendKey, LegendToggle, Money, PageHeader, useToggleSet } from '@/components/finance/ui'
import { usePageRange } from '@/components/finance/RangePicker'
import { NetWorthChart, NetWorthOverlayPills, useNetWorthRows } from '@/components/finance/NetWorthHeroChart'
import { ChartTooltip, crosshair } from '@/components/finance/ChartTooltip'
import { ChartCursorLayer, LinkedCursorToggle, pinnedIndex, useChartCursor, type ChartCursor } from '@/components/finance/chartCursor'
import { timeXAxisProps, timeTickFormatter, yAxisProps, gridProps, CHART_MARGIN, downsample } from '@/components/finance/chartDefaults'

export function NetWorth() {
  const { networth, accounts } = useFinance()
  const [overlays, toggleOverlay] = useToggleSet<string>(['netWorth'])
  // Legend hide/show sets live HERE, not in the cards: a range change resets
  // the staged-mount ladder, which unmounts the cards (placeholder swap) —
  // card-local toggle state died with them, so hidden categories reappeared
  // on every time-window change.
  const [assetsHidden, toggleAssetHidden] = useToggleSet<string>()
  const [debtsHidden, toggleDebtHidden] = useToggleSet<string>()
  // Cash card is opt-IN, not opt-out: it opens as the net line alone and each
  // component band is toggled on. (The other two cards start fully stacked, so
  // their sets track what's HIDDEN — this one tracks what's SHOWN.)
  const [cashShown, toggleCashShown] = useToggleSet<string>()

  const allMonths = networth.months
  const { startMonth, endMonth, control } = usePageRange(
    allMonths[0], allMonths[allMonths.length - 1],
  )
  const months = useMemo(
    () => allMonths.filter((m) => m >= startMonth && m <= endMonth),
    [allMonths, startMonth, endMonth],
  )

  const s = networth.series
  const rows = useNetWorthRows(networth, startMonth, endMonth)
  // One hover + one pinned date shared by every chart on the page.
  const cursor = useChartCursor()

  // The lower charts mount SEQUENTIALLY: each stage waits for the previous
  // one to paint (double rAF), so the hero chart appears immediately instead
  // of the page blocking while four recharts trees build SVG in one commit.
  // A range change resets the ladder — hero repaints first, the rest follow.
  const [stage, setStage] = useState(0)
  useEffect(() => setStage(0), [startMonth, endMonth])
  useEffect(() => {
    if (stage >= 4) return
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setStage((v) => v + 1))
    })
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
    }
  }, [stage])

  const last = latestMonth(allMonths)
  const first = months[0]
  const change = (s.netWorth?.[last] ?? 0) - (s.netWorth?.[first] ?? 0)

  // Tick granularity follows the SELECTED range (day labels for month-scale
  // windows), shared by every chart on the page.
  const tick = timeTickFormatter(startMonth, endMonth)

  // Charts draw at most MAX_CHART_POINTS dates — the daily model piles up
  // thousands, and recharts renders every one as SVG path work.
  const chartMonths = useMemo(() => downsample(months, (m) => s.netWorth?.[m]), [months, s])

  // Per-date, per-account as-of contributions in ONE forward sweep. The
  // composition and retirement charts below all read from this — computing
  // them with per-date contributionAsOf lookups was O(dates × accounts ×
  // balance-keys), which the daily-balance model turned into seconds of
  // main-thread work per render. Deferred until the first staged chart needs
  // it, so the hero paints before this work runs.
  const contributions = useMemo(
    () => (stage >= 1 ? contributionMatrix(accounts, chartMonths) : null),
    [stage, accounts, chartMonths],
  )

  // Asset composition by account group over the selected window.
  const assetGroups = ACCOUNT_GROUPS.filter((g) => !LIABILITY_GROUPS.includes(g))
  const composition = useMemo(() => {
    if (!contributions) return []
    return chartMonths.map((m, mi) => {
      const row: Record<string, number | string | null> = { month: m, t: dateToMs(m) }
      for (const g of assetGroups) row[g] = 0
      accounts.forEach((a, ai) => {
        if (a.isLiability) return
        const g = accountGroupOf(a.type)
        row[g] = (row[g] as number) + contributions[mi][ai]
      })
      return row
    })
  }, [chartMonths, accounts, contributions, assetGroups])

  const debtComposition = useMemo(() => {
    if (!contributions || stage < 2) return []
    return chartMonths.map((m, mi) => {
      const row: Record<string, number | string | null> = { month: m, t: dateToMs(m) }
      for (const g of LIABILITY_GROUPS) row[g] = 0
      accounts.forEach((a, ai) => {
        if (!a.isLiability) return
        const g = accountGroupOf(a.type)
        // stored negative; chart magnitude owed
        row[g] = (row[g] as number) + Math.abs(contributions[mi][ai])
      })
      return row
    })
  }, [chartMonths, accounts, contributions, stage])

  // Cash + equivalents over time — the trend behind the Overview KPI tile and
  // the projection model's `cash` series, which existed as a number but had no
  // chart anywhere. The definition is the app's, NOT a new one (CASH_TYPES in
  // aggregate.ts): liquid assets — checking, savings, taxable brokerage,
  // crypto — LESS credit-card balances. The bands below decompose exactly that,
  // wearing the same fixed group colours the rest of the page uses, and the net
  // line reads straight from the stored series so it can never drift from the
  // KPI it explains.
  const cashComponents = useMemo(
    () => [
      { key: 'Cash', color: accountGroupColor('Cash'), types: ['Checking', 'Savings'] },
      { key: 'Brokerage', color: accountGroupColor('Investments'), types: ['Stock'] },
      // Crypto splits out of the Investments group here, so it can't wear that
      // group's colour — slot 3 is the pick that keeps all four components
      // separable, including for colour-vision deficiency.
      { key: 'Crypto', color: 'var(--series-3)', types: ['Crypto'] },
      { key: 'Credit cards', color: accountGroupColor('Credit Cards'), types: ['Credit'] },
    ],
    [],
  )

  const cashRows = useMemo(() => {
    if (!contributions || stage < 3) return []
    const compOf = new Map<string, string>()
    for (const c of cashComponents) for (const t of c.types) compOf.set(t, c.key)
    return chartMonths.map((m, mi) => {
      const row: Record<string, number | string | null> = { month: m, t: dateToMs(m) }
      for (const c of cashComponents) row[c.key] = 0
      accounts.forEach((a, ai) => {
        const key = compOf.get(a.type)
        if (!key) return
        row[key] = (row[key] as number) + contributions[mi][ai]
      })
      for (const c of cashComponents) row[c.key] = Math.round((row[c.key] as number) * 100) / 100
      // Authoritative net: the same stored series the Overview tile reads.
      row.Net = s.cash?.[m] ?? null
      return row
    })
  }, [chartMonths, accounts, contributions, stage, cashComponents, s])

  // Retirement split: Roth vs non-Roth (pre-tax/traditional) across every
  // Retirement account, classified by name. This is the one place the app keeps
  // the 401k halves apart — elsewhere they aggregate into one balance.
  const retirementSplit = useMemo(() => {
    if (!contributions || stage < 4) return []
    return chartMonths.map((m, mi) => {
      let roth = 0
      let preTax = 0
      accounts.forEach((a, ai) => {
        if (a.type !== 'Retirement') return
        const v = contributions[mi][ai]
        if (v === 0) return
        if (/roth/i.test(a.name)) roth += v
        else preTax += v
      })
      return { month: m, t: dateToMs(m), Roth: Math.round(roth * 100) / 100, 'Pre-tax': Math.round(preTax * 100) / 100 }
    })
  }, [chartMonths, accounts, contributions, stage])
  const hasRetirement = retirementSplit.some((r) => r.Roth !== 0 || r['Pre-tax'] !== 0)

  // Only draw a component that is non-zero somewhere in the window — an empty
  // band is legend noise (no crypto in 2016, no brokerage before it existed).
  const cashBands = useMemo(
    () => cashComponents.filter((c) => cashRows.some((r) => ((r[c.key] as number) ?? 0) !== 0)),
    [cashComponents, cashRows],
  )
  const cashLatest = cashRows[cashRows.length - 1]

  return (
    <div className="space-y-4">
      <PageHeader title="Net worth">{control}</PageHeader>

      <Card>
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <span className="text-3xl font-semibold tracking-tight">{fmtUsd(s.netWorth?.[last] ?? 0)}</span>
            <span className="ml-3 text-sm">
              <Money value={change} compact />{' '}
              <span className="text-ink-3">since {fmtDate(first)}</span>
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {/* Overlay toggles — net worth always shows (fixed pill); tap to add */}
            <NetWorthOverlayPills overlays={overlays} onToggle={toggleOverlay} />
            {/* Linked cursor across all five charts — off by default, persisted. */}
            <LinkedCursorToggle cursor={cursor} />
          </div>
        </div>

        <div className="h-80">
          <NetWorthChart rows={rows} overlays={overlays} startMonth={startMonth} endMonth={endMonth} minTickGap={48} cursor={cursor} />
        </div>
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        {stage >= 1 ? (
          <CompositionCard
            title="Assets by group"
            data={composition}
            groups={assetGroups.filter((g) =>
              composition.some((r) => ((r[g] as number) ?? 0) > 0),
            )}
            tickFormatter={tick}
            hidden={assetsHidden}
            onToggleHidden={toggleAssetHidden}
            cursor={cursor}
          />
        ) : (
          <ChartCardPlaceholder title="Assets by group" />
        )}
        {stage >= 2 ? (
          <CompositionCard
            title="Debts by group"
            data={debtComposition}
            groups={LIABILITY_GROUPS.filter((g) =>
              debtComposition.some((r) => ((r[g] as number) ?? 0) > 0),
            )}
            tickFormatter={tick}
            hidden={debtsHidden}
            onToggleHidden={toggleDebtHidden}
            cursor={cursor}
          />
        ) : (
          <ChartCardPlaceholder title="Debts by group" />
        )}
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        {stage >= 3 ? (
          <CashCard
            data={cashRows}
            bands={cashBands}
            net={(cashLatest?.Net as number) ?? 0}
            asOf={cashLatest?.month as string | undefined}
            tickFormatter={tick}
            shown={cashShown}
            onToggleShown={toggleCashShown}
            cursor={cursor}
          />
        ) : (
          <ChartCardPlaceholder title="Cash + equivalents" />
        )}
        {stage >= 4 ? (
          hasRetirement && <RetirementSplitCard data={retirementSplit} tickFormatter={tick} cursor={cursor} />
        ) : (
          <ChartCardPlaceholder title="Retirement: Roth vs pre-tax" />
        )}
      </div>
    </div>
  )
}

/** Order stacked bands largest-first so the biggest, most stable section sits
 * at the BOTTOM of the stack (recharts renders the first <Area> at the base).
 * Size is judged by each band's value at the most recent point in the window. */
function orderBySizeDesc(keys: readonly string[], data: Record<string, number | string | null>[]): string[] {
  const last = data[data.length - 1] ?? {}
  return [...keys].sort((a, b) => ((last[b] as number) ?? 0) - ((last[a] as number) ?? 0))
}

/** Same-size stand-in a staged chart occupies until its turn — keeps the page
 * from reflowing as charts pop in one by one. */
function ChartCardPlaceholder({ title }: { title: string }) {
  return (
    <Card title={title}>
      <div className="h-64 animate-pulse rounded-xl bg-surface-3/50" />
    </Card>
  )
}

// Roth vs pre-tax retirement balances over time — the Roth share is what's
// already been taxed (tax-free in retirement), so seeing the two side by side
// tracks tax diversification. Stacked so the top edge is total retirement.
const RETIREMENT_SPLIT_SERIES = [
  { key: 'Roth', color: 'var(--series-2)' },
  { key: 'Pre-tax', color: 'var(--series-5)' },
] as const

function RetirementSplitCard({
  data,
  tickFormatter,
  cursor,
}: {
  data: { month: string; t: number; Roth: number; 'Pre-tax': number }[]
  tickFormatter: (ms: number) => string
  cursor: ChartCursor
}) {
  // Split shown is as-of the LATEST point in the visible window (data is the
  // range-filtered series), labeled with that date so it's unambiguous.
  const latest = data[data.length - 1]
  const total = latest ? latest.Roth + latest['Pre-tax'] : 0
  const rothPct = total ? Math.round((latest.Roth / total) * 100) : 0
  // Largest half at the bottom; y-axis pinned at 0 like the group composition charts.
  const keys = RETIREMENT_SPLIT_SERIES.map((s) => s.key)
  const order = orderBySizeDesc(keys, data as Record<string, number | string | null>[])
  const stacked = order.map((k) => RETIREMENT_SPLIT_SERIES.find((s) => s.key === k)!)
  return (
    <Card
      title="Retirement: Roth vs pre-tax"
      subtitle={
        total && latest
          ? `${rothPct}% Roth · ${100 - rothPct}% pre-tax · as of ${fmtDate(latest.month)}`
          : undefined
      }
    >
      <div className="mb-2 flex flex-wrap gap-3">
        {RETIREMENT_SPLIT_SERIES.map((s) => (
          <LegendKey key={s.key} color={s.color} label={s.key} />
        ))}
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart key={cursor.resetKey} data={data} margin={CHART_MARGIN} {...cursor.chartProps}>
            <CartesianGrid {...gridProps} />
            <XAxis {...timeXAxisProps} tickFormatter={tickFormatter} minTickGap={48} />
            <YAxis {...yAxisProps} tickFormatter={(v: number) => fmtUsdCompact(v)} />
            {/* Total row = Roth + pre-tax (the stacked top edge). */}
            <Tooltip cursor={crosshair} defaultIndex={pinnedIndex(data, cursor.pinnedT)} content={<ChartTooltip labelFormatter={fmtMsDate} showTotal />} />
            {stacked.map((s) => (
              <Area     key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.key}
                stackId="1"
                stroke={s.color}
                strokeWidth={1.5}
                fill={s.color}
                fillOpacity={0.35}
                dot={false}
                isAnimationActive={false}
              />
            ))}
            <ChartCursorLayer cursor={cursor} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  )
}

/** Cash + equivalents over time — the trend behind the Overview KPI, which had
 * no chart anywhere. Opens as the net line ALONE (the headline is the net
 * figure; the parts are there when you want them) and each component band is
 * toggled on from the legend. The dark net line is the stored series itself,
 * so it always equals the KPI, whatever is toggled. Card balances stack BELOW
 * zero (stackOffset="sign"), which is also what keeps the red band readable
 * against the green one for deuteranopes — they never touch. */
function CashCard({
  data,
  bands,
  net,
  asOf,
  tickFormatter,
  shown: shownKeys,
  onToggleShown,
  cursor,
}: {
  data: Record<string, number | string | null>[]
  bands: { key: string; color: string }[]
  net: number
  asOf?: string
  tickFormatter: (ms: number) => string
  /** Which component bands to draw — empty means the net line alone. */
  shown: Set<string>
  onToggleShown: (k: string) => void
  cursor: ChartCursor
}) {
  const shown = bands.filter((b) => shownKeys.has(b.key))
  return (
    <Card
      title="Cash + equivalents"
      subtitle={asOf ? `${fmtUsd(net)} net · cash, brokerage, crypto, less card balances · as of ${fmtDate(asOf)}` : undefined}
    >
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <LegendKey color="var(--text-primary)" label="Net" />
        {bands.map((b) => (
          <LegendToggle
            key={b.key}
            label={b.key}
            color={b.color}
            hidden={!shownKeys.has(b.key)}
            onToggle={() => onToggleShown(b.key)}
          />
        ))}
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart key={cursor.resetKey} data={data} margin={CHART_MARGIN} stackOffset="sign" {...cursor.chartProps}>
            <CartesianGrid {...gridProps} />
            <XAxis {...timeXAxisProps} tickFormatter={tickFormatter} minTickGap={48} />
            <YAxis {...yAxisProps} tickFormatter={(v: number) => fmtUsdCompact(v)} />
            <Tooltip cursor={crosshair} defaultIndex={pinnedIndex(data, cursor.pinnedT)} content={<ChartTooltip labelFormatter={fmtMsDate} />} />
            {shown.map((b) => (
              <Area
                key={b.key}
                type="monotone"
                dataKey={b.key}
                name={b.key}
                stackId="1"
                stroke={b.color}
                strokeWidth={1.5}
                fill={b.color}
                fillOpacity={0.35}
                dot={false}
                isAnimationActive={false}
              />
            ))}
            {/* Net rides on top of the bands, not in the stack. */}
            <Area
              type="monotone"
              dataKey="Net"
              name="Net"
              stroke="var(--text-primary)"
              strokeWidth={2}
              fill="none"
              dot={false}
              isAnimationActive={false}
            />
            <ChartCursorLayer cursor={cursor} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  )
}

function CompositionCard({
  title,
  data,
  groups,
  tickFormatter,
  hidden,
  onToggleHidden,
  cursor,
}: {
  title: string
  data: Record<string, number | string | null>[]
  groups: readonly string[]
  tickFormatter: (ms: number) => string
  /** Click a group in the legend to hide/show its band — state is owned by
   * the page (this card unmounts on every range change). */
  hidden: Set<string>
  onToggleHidden: (g: string) => void
  cursor: ChartCursor
}) {
  const shown = groups.filter((g) => !hidden.has(g))
  // Largest band at the bottom; y-axis pinned at 0 so band areas read true.
  const stacked = orderBySizeDesc(shown, data)
  // Headline: the stack's latest total over the VISIBLE bands (hiding a band
  // drops it from the number too, so the subtitle always matches the chart).
  const last = data.at(-1)
  const latestTotal = last ? shown.reduce((sum, g) => sum + ((last[g] as number) ?? 0), 0) : null
  const latestMs = last ? Number(last[timeXAxisProps.dataKey]) : NaN

  return (
    <Card
      title={title}
      subtitle={
        latestTotal == null
          ? undefined
          : `${fmtUsd(latestTotal)} across ${shown.length} ${shown.length === 1 ? 'group' : 'groups'}${Number.isFinite(latestMs) ? ` · as of ${fmtMsDate(String(latestMs))}` : ''}`
      }
    >
      <div className="mb-2 flex flex-wrap gap-1.5">
        {groups.map((g) => (
          <LegendToggle
            key={g}
            label={g}
            color={accountGroupColor(g as never)}
            hidden={hidden.has(g)}
            onToggle={() => onToggleHidden(g)}
          />
        ))}
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart key={cursor.resetKey} data={data} margin={CHART_MARGIN} {...cursor.chartProps}>
            <CartesianGrid {...gridProps} />
            <XAxis {...timeXAxisProps} tickFormatter={tickFormatter} minTickGap={48} />
            <YAxis {...yAxisProps} tickFormatter={(v: number) => fmtUsdCompact(v)} />
            <Tooltip cursor={crosshair} defaultIndex={pinnedIndex(data, cursor.pinnedT)} content={<ChartTooltip labelFormatter={fmtMsDate} showTotal />} />
            {stacked.map((g) => (
              <Area     key={g}
                type="monotone"
                dataKey={g}
                name={g}
                stackId="1"
                stroke={accountGroupColor(g as never)}
                strokeWidth={1.5}
                fill={accountGroupColor(g as never)}
                fillOpacity={0.35}
                dot={false}
                isAnimationActive={false}
              />
            ))}
            <ChartCursorLayer cursor={cursor} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  )
}
